import { ArrowLeft, Pencil, Sparkles } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ReadOnlyContentDisplay } from '@/components/read-only-content-display';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import type { StudentSubmissionItem } from '@/state/course';

interface CourseAssignmentSubmissionSheetProps {
  open: boolean;
  canEdit: boolean;
  hasAIModule: boolean;
  isAIFeedbackLoading: boolean;
  selectedSubmission: StudentSubmissionItem | null;
  selectedSubmissionOwnFeedback: { id: number } | null;
  selectedAssignmentDeadline?: string | null;
  isSubmissionReviewed: (submission: StudentSubmissionItem) => boolean;
  isLateSubmission: (submissionDate?: string | null, deadline?: string | null) => boolean;
  formatAssignmentDeadline: (value?: string | null) => string;
  getInitials: (firstName?: string, lastName?: string) => string;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
  onOpenGradeDialog: (submission: StudentSubmissionItem) => void;
  onOpenAIFeedback: (submission: StudentSubmissionItem) => void;
}

const CourseAssignmentSubmissionSheet = ({
  open,
  canEdit,
  hasAIModule,
  isAIFeedbackLoading,
  selectedSubmission,
  selectedSubmissionOwnFeedback,
  selectedAssignmentDeadline,
  isSubmissionReviewed,
  isLateSubmission,
  formatAssignmentDeadline,
  getInitials,
  onOpenChange,
  onClose,
  onOpenGradeDialog,
  onOpenAIFeedback,
}: CourseAssignmentSubmissionSheetProps) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full p-0 sm:max-w-[760px]" showClose={false}>
        <SheetHeader className="border-b px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <SheetTitle>Submission details</SheetTitle>
              <div className="flex items-center gap-2">
                {canEdit && hasAIModule && selectedSubmission && !selectedSubmissionOwnFeedback ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onOpenAIFeedback(selectedSubmission)}
                    disabled={isAIFeedbackLoading}
                  >
                    <Sparkles className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">AI feedback</span>
                  </Button>
                ) : null}
                {canEdit && selectedSubmission ? (
                  selectedSubmissionOwnFeedback ? (
                    <button
                    type="button"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-primary transition-colors hover:bg-primary/15"
                    onClick={() => onOpenGradeDialog(selectedSubmission)}
                  >
                    <Pencil className="h-4 w-4" />
                    <span className="sr-only">Edit feedback</span>
                  </button>
                ) : (
                  <Button type="button" size="sm" onClick={() => onOpenGradeDialog(selectedSubmission)}>
                    Grade this answer
                  </Button>
                )
              ) : null}
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted"
                onClick={onClose}
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </button>
            </div>
          </div>
        </SheetHeader>
        <div className="max-h-[calc(100vh-72px)] space-y-4 overflow-y-auto p-4">
          {selectedSubmission ? (
            <>
              <div className="rounded-lg border bg-background p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={selectedSubmission.submitted_by.photo_url || undefined} />
                      <AvatarFallback>
                        {getInitials(
                          selectedSubmission.submitted_by.first_name,
                          selectedSubmission.submitted_by.last_name
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-base font-semibold">
                        {selectedSubmission.submitted_by.first_name} {selectedSubmission.submitted_by.last_name}
                      </p>
                      <p className="text-sm text-muted-foreground">{selectedSubmission.submitted_by.email}</p>
                    </div>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p className="font-medium">{formatAssignmentDeadline(selectedSubmission.submission_date)}</p>
                    <div className="flex flex-wrap items-center gap-2">
                      {isSubmissionReviewed(selectedSubmission) ? (
                        <span className="rounded-md border border-green-300 bg-green-50 px-2 py-0.5 text-xs text-green-700 dark:border-green-900/60 dark:bg-green-950/30 dark:text-green-300">
                          Reviewed
                        </span>
                      ) : (
                        <span className="rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300">
                          Pending
                        </span>
                      )}
                      {isLateSubmission(selectedSubmission.submission_date, selectedAssignmentDeadline) ? (
                        <span className="rounded-md border border-red-300 bg-red-50 px-2 py-0.5 text-xs text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
                          Late submission
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2 rounded-lg border bg-background p-4">
                <h4 className="text-sm font-semibold">Submission details</h4>
                {selectedSubmission.assignment_details ? (
                  <ReadOnlyContentDisplay
                    html={selectedSubmission.assignment_details}
                    className="[&_.editor-input]:!min-h-0 [&_.editor-input]:!p-0 [&_.editor-input]:text-sm"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">No details submitted.</p>
                )}
              </div>

              <div className="space-y-2 rounded-lg border bg-background p-4">
                <h4 className="text-sm font-semibold">Attachment</h4>
                {selectedSubmission.assignment_file ? (
                  <a
                    href={selectedSubmission.assignment_file}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-primary hover:underline"
                  >
                    View submission file
                  </a>
                ) : (
                  <p className="text-sm text-muted-foreground">No attachment uploaded.</p>
                )}
              </div>

              <div className="space-y-3 rounded-lg border bg-background p-4">
                <h4 className="text-sm font-semibold">Feedback</h4>
                {selectedSubmission.student_submission.length > 0 ? (
                  <div className="space-y-3">
                    {selectedSubmission.student_submission.map((feedbackItem) => (
                      <div key={feedbackItem.id} className="rounded-md border bg-muted/20 p-3">
                        <div className="mb-2 flex items-center gap-2">
                          <Avatar className="h-7 w-7">
                            <AvatarImage src={feedbackItem.feedback_provided_by.photo_url || undefined} />
                            <AvatarFallback>
                              {getInitials(
                                feedbackItem.feedback_provided_by.first_name,
                                feedbackItem.feedback_provided_by.last_name
                              )}
                            </AvatarFallback>
                          </Avatar>
                          <p className="text-sm font-medium">
                            {feedbackItem.feedback_provided_by.first_name} {feedbackItem.feedback_provided_by.last_name}
                          </p>
                        </div>
                        {feedbackItem.feedback ? (
                          <ReadOnlyContentDisplay
                            html={feedbackItem.feedback}
                            className="[&_.editor-input]:!min-h-0 [&_.editor-input]:!p-0 [&_.editor-input]:text-sm"
                          />
                        ) : (
                          <p className="text-sm text-muted-foreground">No feedback message.</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No feedback added yet.</p>
                )}
              </div>
            </>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CourseAssignmentSubmissionSheet;
